import React, { useState, useContext, useEffect, useCallback } from "react";
import CustomInput from "../../components/CustomInput";
import { Editor } from "primereact/editor";
import { toast } from "react-toastify";
import Loading from "../../components/Loading";
import { firebaseService } from "../../../../services/api/firebaseService";
import { AuthContext } from "../../../../contexts/AuthContext";
import { postsService } from "../../../../services/api/postsService";
import Modal from "../../components/Modal";
import DragDrop from "../../components/DragDrop";
import { removeAccents } from "../../../../utils/removeAccents";
import { getCleanFileName } from "../../../../utils/fileUtils";

const PostPopup = ({ mode = "add", post_id, closePopup, onSuccess, handleAddPost }) => {
  const isEdit = mode === "edit";
  const { token } = useContext(AuthContext);

  const [loading, setLoading] = useState(false);
  const [data, setData] = useState({ name: "", txtUrl: "", fileUrl: "", status: "Hoạt động" });
  const [selectedFile, setSelectedFile] = useState(null);
  const [isEditingFile, setIsEditingFile] = useState(false);

  const fetchData = useCallback(async () => {
    if (!isEdit || !post_id) return;
    try {
      setLoading(true);
      const response = await postsService.get_by_id(post_id, token);
      const payload = response.data;

      let txtUrl = payload.txtUrl || "";
      let fileUrl = payload.fileUrl || "";

      if (txtUrl && !fileUrl && !txtUrl.trim().startsWith("<")) {
        fileUrl = txtUrl;
        txtUrl = "";
      }

      setData({
        name: payload.name || "",
        txtUrl: txtUrl,
        fileUrl: fileUrl,
        status: payload.status || "Hoạt động",
      });
    } catch (error) {
      toast.error(error.response?.data?.message || "Không thể tải dữ liệu bài viết");
      console.error("Error fetching post:", error);
    } finally {
      setLoading(false);
    }
  }, [post_id, token, isEdit]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const extractFirebaseImageUrls = (htmlContent) => {
    if (!htmlContent || typeof htmlContent !== "string") return [];
    const parser = new DOMParser();
    const doc = parser.parseFromString(htmlContent, "text/html");
    const images = doc.querySelectorAll("img");
    const firebaseUrls = [];
    images.forEach((img) => {
      const src = img.getAttribute("src");
      if (src && (src.startsWith("https://storage.googleapis.com/") || src.startsWith("https://firebasestorage.googleapis.com/"))) {
        firebaseUrls.push(src);
      }
    });
    return firebaseUrls;
  };

  const processImagesInContent = async (htmlContent) => {
    if (!htmlContent || typeof htmlContent !== "string") return htmlContent || "";
    const parser = new DOMParser();
    const doc = parser.parseFromString(htmlContent, "text/html");
    const images = doc.querySelectorAll("img");
    if (images.length === 0) return htmlContent;

    for (const img of images) {
      const src = img.getAttribute("src");
      if (!src || src.startsWith("https://storage.googleapis.com/") || src.startsWith("https://firebasestorage.googleapis.com/")) {
        continue;
      }
      if (src.startsWith("data:image/")) {
        try {
          const fetchRes = await fetch(src);
          const blob = await fetchRes.blob();
          const fileName = `editor-image-${Date.now()}.png`;
          const file = new File([blob], fileName, { type: blob.type });
          const uploadRes = await firebaseService.upload_file(token, file);
          if (uploadRes?.url) img.setAttribute("src", uploadRes.url);
        } catch (error) {
          console.error("Error uploading inline image:", error);
        }
      }
    }
    return doc.body.innerHTML;
  };

  const handleSubmit = async () => {
    if (!data.name.trim()) {
      toast.warning("Vui lòng nhập tên bài viết.");
      return;
    }

    try {
      setLoading(true);

      let oldFileUrl = "";
      let oldImagesInContent = [];

      if (isEdit) {
        const originalRes = await postsService.get_by_id(post_id, token);
        oldFileUrl = originalRes.data.fileUrl || "";
        if (originalRes.data.txtUrl && originalRes.data.txtUrl.trim().startsWith("<")) {
          oldImagesInContent = extractFirebaseImageUrls(originalRes.data.txtUrl);
        }
      }

      const processedContent = await processImagesInContent(data.txtUrl);

      let updatedFileUrl = data.fileUrl;
      if (selectedFile) {
        const fileExtension = selectedFile.name.split(".").pop();
        const newFileName = `${removeAccents(data.name)}.${fileExtension}`;
        const renamedFile = new File([selectedFile], newFileName, { type: selectedFile.type });

        const uploadRes = await firebaseService.upload_file(token, renamedFile);
        if (uploadRes?.url) {
          updatedFileUrl = uploadRes.url;
          if (isEdit && oldFileUrl) {
            try { await firebaseService.delete_file(token, oldFileUrl); } catch (e) { console.warn("Lỗi xóa file cũ:", e); }
          }
        }
      } else if (isEdit && isEditingFile && !data.fileUrl && oldFileUrl) {
        try { await firebaseService.delete_file(token, oldFileUrl); } catch (e) { console.warn("Lỗi xóa file cũ:", e); }
        updatedFileUrl = "";
      }

      const payload = {
        name: data.name,
        txtUrl: processedContent || updatedFileUrl,
        fileUrl: updatedFileUrl,
        status: data.status,
      };

      if (isEdit) {
        await postsService.update(post_id, payload, token);
        const newImages = extractFirebaseImageUrls(processedContent);
        const imagesToDelete = oldImagesInContent.filter(url => !newImages.includes(url));
        for (const imgUrl of imagesToDelete) {
          try { await firebaseService.delete_file(token, imgUrl); } catch (e) { console.warn("Lỗi xóa ảnh cũ:", e); }
        }
        toast.success("Cập nhật bài viết thành công!");
      } else {
        if (handleAddPost) {
          await handleAddPost(payload);
        } else {
          await postsService.create(payload, token);
          toast.success("Thêm bài viết thành công!");
        }
      }

      if (onSuccess) onSuccess();
      closePopup();
    } catch (error) {
      console.error("Error submitting post:", error);
      toast.error(error.response?.data?.message || "Đã xảy ra lỗi hệ thống");
    } finally {
      setLoading(false);
    }
  };


  if (loading && isEdit && !data.name) {
    return (
      <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-[80]">
        <div className="bg-white p-8 rounded-lg shadow-xl">
          <Loading />
        </div>
      </div>
    );
  }

  return (
    <Modal
      isOpen={true}
      onClose={closePopup}
      title={isEdit ? "Chỉnh sửa tài liệu" : "Thêm tài liệu mới"}
      maxWidth="900px"
      primaryButtonText={loading ? "Đang xử lý..." : (isEdit ? "Lưu thay đổi" : "Thêm mới")}
      primaryButtonOnClick={handleSubmit}
      showCancelButton={!loading}
      cancelButtonText="Hủy"
    >
      <div className="p-6 space-y-5">
        <div className="flex flex-col gap-1">
          <strong className="text-gray-700">Tên tài liệu:</strong>
          <CustomInput
            placeholder="Nhập tên tài liệu"
            value={data.name}
            required={true}
            onChange={(newValue) => setData({ ...data, name: newValue })}
          />
        </div>

        <div className="flex items-center gap-6 py-1">
          <strong className="text-gray-700 text-sm">Trạng thái:</strong>
          <div className="flex gap-4">
            <label className="flex items-center gap-2 cursor-pointer">
              <input
                type="radio"
                checked={data.status === "Hoạt động"}
                onChange={() => setData({ ...data, status: "Hoạt động" })}
                className="w-4 h-4 text-blue-600 focus:ring-blue-500"
              />
              <span className={`text-sm ${data.status === "Hoạt động" ? "font-bold text-blue-600" : "text-gray-500"}`}>
                Hoạt động
              </span>
            </label>
            <label className="flex items-center gap-2 cursor-pointer">
              <input
                type="radio"
                checked={data.status === "Đã hủy"}
                onChange={() => setData({ ...data, status: "Đã hủy" })}
                className="w-4 h-4 text-red-600 focus:ring-red-500"
              />
              <span className={`text-sm ${data.status === "Đã hủy" ? "font-bold text-red-600" : "text-gray-500"}`}>
                Đã hủy
              </span>
            </label>
          </div>
        </div>

        <div className="flex flex-col gap-2">
          <strong className="text-gray-700">Nội dung tài liệu (Bài viết):</strong>
          <Editor
            value={data.txtUrl}
            onTextChange={(e) => setData({ ...data, txtUrl: e.htmlValue })}
            style={{ height: "250px" }}
            placeholder="Soạn thảo nội dung tài liệu..."
          />
        </div>

        <div className="flex flex-col gap-2">
          <strong className="text-gray-700">File đính kèm:</strong>
          {isEdit && data.fileUrl && !isEditingFile ? (
            <div className="flex justify-between items-center border rounded-lg px-4 py-3 bg-blue-50 border-blue-100">
              <div className="flex items-center gap-2 overflow-hidden">
                <img src="/icons/document-blue.svg" alt="doc" className="w-5 h-5 flex-shrink-0" />
                <span className="truncate text-sm font-medium text-blue-800">
                  {getCleanFileName(data.fileUrl)}
                </span>
              </div>
              <button
                type="button"
                onClick={() => {
                  setIsEditingFile(true);
                  setData({ ...data, fileUrl: "" });
                }}
                className="p-1 hover:bg-red-50 rounded-full transition-all"
                title="Gỡ file đính kèm"
              >
                <img src="/icons/x-close.svg" alt="remove" className="w-4 h-4" />
              </button>
            </div>
          ) : (
            <DragDrop
              label={selectedFile ? selectedFile.name : "Kéo thả hoặc chọn file tải lên"}
              onFileChange={(files) => setSelectedFile(files?.[0] || null)}
              maxFiles={1}
            />
          )}
        </div>
      </div>
    </Modal>
  );
};

export default PostPopup;
